import { useEffect, useRef, useState } from "react"

import { useI18next } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import styled, { css } from "styled-components"

import Button from "components/buttons/Button"
import LanguageButton from "components/buttons/LanguageButton"
import colors from "styles/colors"
import easing from "styles/easing"
import media from "styles/media"
import text from "styles/text"
import { LocaleData } from "types/gatsby-alias"
import { fmobile } from "utils/fullyResponsive"
import { isBrowser } from "utils/functions"
import externalLinks from "utils/links"
import UniversalLink from "utils/Loader/UniversalLink"

import PopoutLinksCardMobile from "./PopoutLinksCardMobile"
import { Section } from "components/Header"
import routes from "utils/routes"

type DataItem = {
  title:
    | "Deposits"
    | "Engagement Channels"
    | "Payments & Cards"
    | "Lending"
    | "Risk"
    | "Platform"
    | "Company"
  description: string
  image: string
  links: Section[]
}

interface HeaderPopoutBoxMobileProps {
  data: DataItem[]
  burgerMode: string
  pathname: string
  translation: LocaleData
  languageOverride?: string
}

export default function HeaderPopoutBoxMobile({
  data,
  burgerMode,
  pathname,
  translation,
  languageOverride,
}: HeaderPopoutBoxMobileProps) {
  data = data.map((ele)=>{
    return {
      ...ele,
      links:[...ele.links],
    }
  })
  const [height, setHeight] = useState(0)
  const slideRef = useRef(null)
  
  const UpdateHeight = () => {
    setHeight(window.innerHeight)
  }

  const toggleSlider = (burgerM: string) => {
    if (burgerM !== "open")
      gsap.to(slideRef.current, {
        duration: 0.3,
        x: "100vw",
        ease: easing.main,
      })

    if (burgerM === "open")
      gsap.to(slideRef.current, {
        duration: 0.3,
        x: "2.667vw",
        ease: easing.main,
      })
  }

  useEffect(() => {
    toggleSlider(burgerMode)
  }, [burgerMode])

  useEffect(() => {
    UpdateHeight()
    if (isBrowser()) window.addEventListener("resize", UpdateHeight)

    return () => {
      if (isBrowser()) window.removeEventListener("resize", UpdateHeight)
    }
  }, [])
  function getDataSectionWise(item: any) {
    if (
      item.title === "Deposits" ||
      item.title === "Engagement Channels" ||
      item.title === "Payments & Cards" ||
      item.title === "Lending"
    ) {
      return item.links.slice(0, item.links.length - 2)
    } else if (item.title === "Risk") {
      return item.links.slice(0, item.links.length - 3)
    } else if (item.title === "Platform") {
      item.links = item.links.slice(0, item.links.length - 1)
      item.links.push({ title: "Services", links: routes.services })
      return item.links
    }
    return item.links.slice(0, item.links.length - 1)
  }
  return (
    <Wrapper ref={slideRef} height={height}>
      <Categories>
        {data.map((item: DataItem) => (
          <Category
            key={item.title}
            icon={item.image}
            title={item.title}
            links={getDataSectionWise(item)}
            translation={translation}
            languageOverride={languageOverride}
          />
        ))}
        <LanguageButton pathname={pathname} />
      </Categories>
      <Gradient />
      <ButtonContainer to={externalLinks.login}>
        <Button>Client Login</Button>
      </ButtonContainer>
    </Wrapper>
  )
}
interface CategoryProps {
  icon: string
  title: string
  links: {
    title: string
    url: string
  }[]
  translation: LocaleData
  languageOverride?: string
}

function Category({
  icon,
  title,
  links,
  translation,
  languageOverride,
}: CategoryProps) {
  const { language } = useI18next()
  return (
    <CategoryWrapper>
      <Row>
        <Icon src={icon} alt="header icon" />
        <Title>{translation[title]}</Title>
      </Row>
      <SubCategoryWrapper>
        {links.map((item: any) => {
          return (
            <PopoutLinksCardMobile
              data={item}
              translation={translation}
              languageOverride={languageOverride}
            />
          )
        })}
      </SubCategoryWrapper>
      <Line />
    </CategoryWrapper>
  )
}

const SubCategoryWrapper = styled.div`
  display: flex;
  widht: 93vw;
  flex-wrap: wrap;
`
const Wrapper = styled.div<{ height: number }>`
  ${media.mobile} {
    display: flex;
  }
  width: 94.667vw;
  height: 100vh;
  flex-shrink: 0;
  top: 4vw;

  gap: 20px;
  display: none;

  background-color: ${colors.blue800};
  padding: 2.667vw 4vw 5.333vw;
  box-shadow: 5.333vw 10.667vw 21.333vw rgba(1, 3, 13, 0.2);
  border-radius: 5.333vw;

  flex-direction: column;

  overflow-y: hidden;
  overflow-x: hidden;
  position: fixed;
  left: 0;
  transform: translateX(100vw);
`

const Categories = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  height: 100vh;
  position: relative;
  z-index: 1;
  ${fmobile(css`
    gap: 5px;
    padding-bottom: 40px;
  `)}
  ::-webkit-scrollbar {
    display: none;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`

const Gradient = styled.div`
  width: 355px;
  height: 200px;
  position: absolute;
  flex-shrink: 0;
  top: 414px;
  background: linear-gradient(180deg, rgba(2, 6, 38, 0) 0%, #020626 56.77%);
`

const ButtonContainer = styled(UniversalLink)`
  display: flex;
  padding: 16px 30px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  background: linear-gradient(
    180deg,
    rgba(2, 6, 38, 0) 0%,
    ${colors.blue800} 56.77%
  );
  z-index: 5000;

  button {
    width: 86.667vw;
    z-index: 6000;
  }
`

const Link = styled(UniversalLink)`
  ${text.buttonR}
  font-size: 3.733vw !important;
  width: 42.667vw;

  color: ${colors.seaFoam400};
`

const CategoryWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
`

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const Icon = styled.img`
  width: 10.667vw;
  aspect-ratio: 1;
`

const Title = styled.div`
  ${text.captionKicker}
  color: ${colors.blue10};
  margin-left: 2.667vw;
`

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.333vw;
  margin-top: 1.333vw;
`

const Line = styled.div`
  margin: 3.633vw 0;
  width: 100%;
  border-bottom: 0.267vw dashed ${colors.blue700};
  height: 1px;
`
